import React, { useEffect, useRef } from 'react';
import { Animated, ViewStyle } from 'react-native';
import styles, { getSkeletonBackgroundColor } from './Skeleton/styles';

export interface SkeletonProps {
  height?: number | string;
  width?: number | string;
  borderRadius?: number;
  animation?: 'wave' | 'pulse' | 'none';
  style?: ViewStyle;
  testID?: string;
  // Additional props for compatibility
  h?: number | string;
  w?: number | string;
  px?: number | string;
  py?: number | string;
  margin?: number | string;
  marginX?: number | string;
  marginY?: number | string;
  marginTop?: number | string;
  marginBottom?: number | string;
  marginLeft?: number | string;
  marginRight?: number | string;
  padding?: number | string;
  paddingX?: number | string;
  paddingY?: number | string;
  paddingTop?: number | string;
  paddingBottom?: number | string;
  paddingLeft?: number | string;
  paddingRight?: number | string;
}

const Skeleton: React.FC<SkeletonProps> = ({
  height,
  width = '100%',
  borderRadius = 4,
  animation = 'wave',
  style,
  testID,
  // Native-base compatibility props
  h,
  w,
  px: _px,
  py: _py,
  margin,
  marginX,
  marginY,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  padding,
  paddingX,
  paddingY,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  ...otherProps
}) => {
  const animatedValue = useRef(new Animated.Value(0)).current;
  const pulseValue = useRef(new Animated.Value(1)).current;

  // Use h prop if height is not provided (native-base compatibility)
  const finalHeight = height || h || 20;
  // Use w prop if width is not provided (native-base compatibility)
  const finalWidth = width || w || '100%';

  useEffect(() => {
    if (animation === 'wave') {
      const animationLoop = Animated.loop(
        Animated.sequence([
          Animated.timing(animatedValue, {
            toValue: 1,
            duration: 1200,
            useNativeDriver: true,
          }),
          Animated.timing(animatedValue, {
            toValue: 0,
            duration: 1200,
            useNativeDriver: true,
          }),
        ]),
      );
      animationLoop.start();
      return () => animationLoop.stop();
    }
    if (animation === 'pulse') {
      const pulseLoop = Animated.loop(
        Animated.sequence([
          Animated.timing(pulseValue, {
            toValue: 0.6,
            duration: 800,
            useNativeDriver: true,
          }),
          Animated.timing(pulseValue, {
            toValue: 1,
            duration: 800,
            useNativeDriver: true,
          }),
        ]),
      );
      pulseLoop.start();
      return () => pulseLoop.stop();
    }
  }, [animation, animatedValue, pulseValue]);

  // Combine all style-related props into a single style object
  const combinedStyle: ViewStyle = {
    ...style,
    ...(margin !== undefined && { margin }),
    ...(marginX !== undefined && { marginHorizontal: marginX }),
    ...(marginY !== undefined && { marginVertical: marginY }),
    ...(marginTop !== undefined && { marginTop }),
    ...(marginBottom !== undefined && { marginBottom }),
    ...(marginLeft !== undefined && { marginLeft }),
    ...(marginRight !== undefined && { marginRight }),
    ...(padding !== undefined && { padding }),
    ...(paddingX !== undefined && { paddingHorizontal: paddingX }),
    ...(paddingY !== undefined && { paddingVertical: paddingY }),
    ...(paddingTop !== undefined && { paddingTop }),
    ...(paddingBottom !== undefined && { paddingBottom }),
    ...(paddingLeft !== undefined && { paddingLeft }),
    ...(paddingRight !== undefined && { paddingRight }),
  };

  const getAnimationStyle = () => {
    if (animation === 'wave') {
      return {
        opacity: animatedValue.interpolate({
          inputRange: [0, 1],
          outputRange: [0.3, 0.8],
        }),
      };
    }
    if (animation === 'pulse') {
      return {
        opacity: pulseValue,
      };
    }
    return {};
  };

  return (
    <Animated.View
      testID={testID}
      style={[
        {
          height: finalHeight,
          width: finalWidth,
          borderRadius,
          backgroundColor: getSkeletonBackgroundColor(),
        },
        styles.skeletonContainer,
        getAnimationStyle(),
        combinedStyle,
      ]}
      {...otherProps}>
      {animation === 'wave' && (
        <Animated.View
          style={[
            styles.waveOverlay,
            {
              transform: [
                {
                  translateX: animatedValue.interpolate({
                    inputRange: [0, 1],
                    outputRange: [-100, 100],
                  }),
                },
              ],
            },
          ]}
        />
      )}
    </Animated.View>
  );
};

// Skeleton.Text component for native-base compatibility
export const SkeletonText: React.FC<SkeletonProps> = props => <Skeleton height={16} borderRadius={2} {...props} />;

export default Skeleton;
